import { observer } from 'mobx-react';
import React from 'react';
import Checkbox from '~/components/commonUi/Checkbox/Checkbox';
import DateTimePicker from '~/components/commonUi/DateTimePicker/DateTimePicker';
import Input from '~/components/commonUi/Input/Input';
import SelectText from '~/components/commonUi/SelectText/SelectText';
import TextArea from '~/components/commonUi/TextArea/TextArea';
import { FORM_FIELDS } from '~/consts/admin';
import { FormFieldConfig } from '~/definitions/admin';

type Props = {
    fieldName: keyof typeof FORM_FIELDS;
    value: any;
    options?: { label: string; value: string }[];
    error?: boolean;
    disabled?: boolean;
    onChange: (fieldName: string, value: any) => void;
};

const DynamicFormField: React.FC<Props> = observer(({ fieldName, value, options = [], error, disabled, onChange }) => {
    const fieldConfig: FormFieldConfig = FORM_FIELDS[fieldName];

    const commonProps = {
        id: fieldName,
        name: fieldName,
        extendedClassName: `w-full ${fieldConfig?.extendedClassName || ''}`.trim(),
        testId: fieldConfig.testId,
        error,
        disabled,
    };

    switch (fieldConfig.type) {
        case 'select':
            return (
                <SelectText
                    {...commonProps}
                    onChange={(e) => onChange(fieldName, e.target.value)}
                    value={value || ''}
                    options={options}
                />
            );
        case 'datetime':
            return (
                <DateTimePicker
                    {...commonProps}
                    value={value}
                    onChange={(val) => onChange(fieldName, val)}
                    allowInput={true}
                />
            );
        case 'number':
            return (
                <Input
                    {...commonProps}
                    type="number"
                    onChange={(e) => onChange(fieldName, e.target.value)}
                    value={value || ''}
                />
            );
        case 'text':
            return (
                <Input
                    {...commonProps}
                    type="text"
                    maxLength={fieldConfig.maxLength}
                    onChange={(e) => onChange(fieldName, e.target.value)}
                    value={value || ''}
                />
            );
        case 'checkbox':
            return (
                <Checkbox
                    {...commonProps}
                    checked={value === 'true'}
                    onChange={(e) => onChange(fieldName, String(e.target.checked))}
                />
            );
        case 'textarea':
            return (
                <TextArea {...commonProps} onChange={(e) => onChange(fieldName, e.target.value)} value={value || ''} />
            );
        default:
            return null;
    }
});

export default DynamicFormField;
